import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Loader2, Check, AlertCircle, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { authApi, adminApi } from '@/lib/api';
import type { User, UserPlan, AdminJob } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Navigation } from '@/components/Navigation';
import { showErrorToast } from '@/lib/toast';

const PAGE_SIZE = 20;

const JOB_STATE_STYLES: Record<string, string> = {
  created:   'bg-gray-100 text-gray-700',
  retry:     'bg-yellow-100 text-yellow-800',
  active:    'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  expired:   'bg-orange-100 text-orange-800',
  cancelled: 'bg-gray-100 text-gray-600',
  failed:    'bg-red-100 text-red-800',
};

function StateBadge({ state }: { state: string }) {
  const cls = JOB_STATE_STYLES[state] ?? 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${cls}`}>
      {state}
    </span>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function truncateOutput(output: unknown): string {
  if (!output) return '';
  const str = typeof output === 'string' ? output : JSON.stringify(output);
  return str.length > 120 ? str.slice(0, 120) + '…' : str;
}

// ── Jobs Tab ────────────────────────────────────────────────────────────────

function JobsTab() {
  const [state, setState] = useState('');
  const [name, setName] = useState('');
  const [hours, setHours] = useState(24);
  const [page, setPage] = useState(0);
  const [selectedJob, setSelectedJob] = useState<AdminJob | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ['admin', 'jobs', state, name, hours, page],
    queryFn: () => adminApi.listJobs({ state: state || undefined, name: name || undefined, hours, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    refetchInterval: 10_000,
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  const handleFilterChange = () => setPage(0);

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">State</label>
              <select
                value={state}
                onChange={(e) => { setState(e.target.value); handleFilterChange(); }}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All states</option>
                <option value="created">created</option>
                <option value="active">active</option>
                <option value="retry">retry</option>
                <option value="completed">completed</option>
                <option value="failed">failed</option>
                <option value="expired">expired</option>
                <option value="cancelled">cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Job name</label>
              <input
                type="text"
                placeholder="Filter by name…"
                value={name}
                onChange={(e) => { setName(e.target.value); handleFilterChange(); }}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Window</label>
              <select
                value={hours}
                onChange={(e) => { setHours(parseInt(e.target.value)); handleFilterChange(); }}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value={1}>Last 1h</option>
                <option value={6}>Last 6h</option>
                <option value={24}>Last 24h</option>
                <option value={168}>Last 7d</option>
              </select>
            </div>
            {isFetching && <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground self-center" />}
          </div>
        </CardContent>
      </Card>

      {/* Jobs table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">Name</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">State</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">Created</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">Started</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">Completed</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">Error / Output</th>
                </tr>
              </thead>
              <tbody>
                {data?.jobs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No jobs found</td>
                  </tr>
                )}
                {data?.jobs.map((job: AdminJob) => (
                  <tr
                    key={job.id}
                    onClick={() => setSelectedJob(job)}
                    className="border-b last:border-0 hover:bg-muted/20 cursor-pointer"
                  >
                    <td className="px-4 py-2 font-mono text-xs">{job.name}</td>
                    <td className="px-4 py-2"><StateBadge state={job.state} /></td>
                    <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(job.createdOn)}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(job.startedOn)}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(job.completedOn)}</td>
                    <td className="px-4 py-2 text-xs text-red-700 font-mono max-w-xs truncate">
                      {(job.state === 'failed' || job.state === 'expired') ? truncateOutput(job.output) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => p - 1)} disabled={page === 0}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">Page {page + 1} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Job detail dialog */}
      <Dialog open={!!selectedJob} onOpenChange={(open) => { if (!open) setSelectedJob(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm">{selectedJob?.name}</DialogTitle>
            <div className="flex items-center gap-3 pt-1">
              {selectedJob && <StateBadge state={selectedJob.state} />}
              <span className="text-xs text-muted-foreground">Created {fmtDate(selectedJob?.createdOn ?? null)}</span>
              {selectedJob?.completedOn && (
                <span className="text-xs text-muted-foreground">Completed {fmtDate(selectedJob.completedOn)}</span>
              )}
            </div>
          </DialogHeader>
          <div className="overflow-y-auto space-y-4 flex-1 min-h-0">
            {selectedJob?.output != null && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Output</p>
                <pre className="text-xs bg-muted rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
                  {typeof selectedJob.output === 'string'
                    ? selectedJob.output
                    : JSON.stringify(selectedJob.output, null, 2)}
                </pre>
              </div>
            )}
            {selectedJob?.data != null && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Input data</p>
                <pre className="text-xs bg-muted rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
                  {typeof selectedJob.data === 'string'
                    ? selectedJob.data
                    : JSON.stringify(selectedJob.data, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Users Tab ───────────────────────────────────────────────────────────────

function UsersTab() {
  const queryClient = useQueryClient();
  const detailRef = useRef<HTMLDivElement>(null);

  const [page, setPage] = useState(0);
  const [searchEmail, setSearchEmail] = useState('');
  const [selectedUser, setSelectedUser] = useState<(User & { plan: UserPlan }) | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', page],
    queryFn: () => adminApi.listUsers(PAGE_SIZE, page * PAGE_SIZE),
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  const searchMutation = useMutation({
    mutationFn: (email: string) => adminApi.findUserByEmail(email),
    onSuccess: (user) => {
      setSelectedUser(user);
      setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    },
    onError: () => {
      showErrorToast('User not found');
      setSelectedUser(null);
    },
  });

  const grantMutation = useMutation({
    mutationFn: ({ userId, amount, note }: { userId: string; amount: number; note?: string }) =>
      adminApi.grantCredits(userId, amount, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  const subMutation = useMutation({
    mutationFn: ({ userId, tier, monthlyCredits }: { userId: string; tier: string; monthlyCredits: number }) =>
      adminApi.setSubscription(userId, tier, monthlyCredits),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  const handleRowClick = (user: User & { plan: UserPlan }) => {
    setSelectedUser(user);
    setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchEmail.trim()) searchMutation.mutate(searchEmail.trim());
  };

  const handleGrantCredits = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    const form = e.target as HTMLFormElement;
    const amount = parseInt((form.querySelector('[data-grant-amount]') as HTMLInputElement).value || '0');
    const note = (form.querySelector('[data-grant-note]') as HTMLTextAreaElement)?.value;
    if (amount > 0) grantMutation.mutate({ userId: selectedUser.id, amount, note });
  };

  const handleSetSubscription = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    const form = e.target as HTMLFormElement;
    const tier = (form.querySelector('[data-sub-tier]') as HTMLSelectElement).value;
    const monthlyCredits = parseInt((form.querySelector('[data-sub-monthly]') as HTMLInputElement).value || '0');
    if (tier && monthlyCredits >= 0) subMutation.mutate({ userId: selectedUser.id, tier, monthlyCredits });
  };

  return (
    <div className="space-y-6">
      {/* User list table */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">All Users</h2>
          {data && <p className="text-sm text-muted-foreground">{data.total} total</p>}
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">User</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">Created</th>
                  <th className="px-4 py-2 text-left font-medium text-muted-foreground">Plan</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…
                    </td>
                  </tr>
                )}
                {data?.users.map((user) => (
                  <tr
                    key={user.id}
                    onClick={() => handleRowClick(user)}
                    className={`border-b last:border-0 cursor-pointer hover:bg-muted/30 transition-colors ${selectedUser?.id === user.id ? 'bg-primary/5' : ''}`}
                  >
                    <td className="px-4 py-2">
                      {user.displayName && <p className="font-medium">{user.displayName}</p>}
                      <p className={user.displayName ? 'text-xs text-muted-foreground' : ''}>{user.email}</p>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 capitalize">
                      {user.plan?.subscriptionTier ?? 'free'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => p - 1)} disabled={page === 0}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">Page {page + 1} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Search */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Find User by Email</h2>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="email"
              placeholder="Enter user email..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <Button type="submit" disabled={searchMutation.isPending}>
              {searchMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4 mr-2" />
              )}
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* User detail */}
      <div ref={detailRef}>
        {selectedUser ? (
          <>
            <Card className="mb-6">
              <CardHeader>
                <h2 className="text-lg font-semibold">{selectedUser.displayName || selectedUser.email}</h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="text-sm font-medium">{selectedUser.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Created</p>
                    <p className="text-sm font-medium">{new Date(selectedUser.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Credits Balance</p>
                    <p className="text-sm font-medium text-green-600">{selectedUser.plan.creditsBalance}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Subscription Tier</p>
                    <p className="text-sm font-medium">{selectedUser.plan.subscriptionTier || 'Free'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader>
                <h2 className="text-lg font-semibold">Grant Credits</h2>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleGrantCredits} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Amount</label>
                    <input
                      type="number"
                      min="1"
                      data-grant-amount
                      placeholder="Enter credit amount..."
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Note (optional)</label>
                    <textarea
                      data-grant-note
                      placeholder="e.g., Monthly grant, bonus credits..."
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                      rows={2}
                    />
                  </div>
                  <Button type="submit" disabled={grantMutation.isPending} className="w-full">
                    {grantMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Grant Credits
                  </Button>
                  {grantMutation.isSuccess && (
                    <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-2 rounded">
                      <Check className="h-4 w-4" />
                      Credits granted successfully
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h2 className="text-lg font-semibold">Set Subscription</h2>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSetSubscription} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Tier</label>
                    <select
                      data-sub-tier
                      defaultValue={selectedUser.plan.subscriptionTier || 'free'}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="free">Free</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Monthly Credits</label>
                    <input
                      type="number"
                      min="0"
                      data-sub-monthly
                      defaultValue={selectedUser.plan.monthlyCredits}
                      placeholder="0"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <Button type="submit" disabled={subMutation.isPending} className="w-full">
                    {subMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Update Subscription
                  </Button>
                  {subMutation.isSuccess && (
                    <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-2 rounded">
                      <Check className="h-4 w-4" />
                      Subscription updated successfully
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>
          </>
        ) : (
          <Card>
            <CardContent className="pt-8">
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <AlertCircle className="h-4 w-4" />
                <p>Click a user in the list or search by email to manage them</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ── Admin Page ──────────────────────────────────────────────────────────────

export default function AdminPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'users' | 'jobs'>('users');

  const { data: authData, isLoading: authLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authApi.me,
    retry: false,
  });

  if (!authLoading && !authData) {
    void navigate('/login');
    return null;
  }

  if (!authLoading && !authData?.user?.isAdmin) {
    void navigate('/capabilities');
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="mx-auto max-w-5xl px-4 py-8">
        <h1 className="text-3xl font-bold mb-2">Admin Panel</h1>
        <p className="text-muted-foreground mb-6">Manage users, grant credits, and monitor jobs.</p>

        {/* Tab bar */}
        <div className="flex border-b mb-6">
          {(['users', 'jobs'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
                tab === t
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 'users' && <UsersTab />}
        {tab === 'jobs' && <JobsTab />}
      </main>
    </div>
  );
}
